import React from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import BaseDialog from "../../../utils/dialog/BaseDialog";
import {
  ASSET_EXPENSED_OFF_CATEGORY,
  isAssetExpensedOffCategory,
} from "./rules";

const FaRegisterCategoryDialog = ({
  open,
  onClose,
  assetCategories = [],
  rows = [],
  newCategoryName,
  categoryError,
  onCategoryNameChange,
  onAddCategory,
  onRemoveCategory,
}) => {
  const normalizedNewCategoryName = String(newCategoryName || "")
    .trim()
    .toLowerCase();
  const hasDuplicateCategory =
    normalizedNewCategoryName &&
    assetCategories.some(
      (category) =>
        String(category.assetCategory || "").trim().toLowerCase() ===
        normalizedNewCategoryName
    );
  const isReservedCategory = isAssetExpensedOffCategory(newCategoryName);
  const validationMessage = isReservedCategory
    ? `${ASSET_EXPENSED_OFF_CATEGORY} is a system category. Select it from the asset category dropdown instead.`
    : hasDuplicateCategory
      ? "This category already exists."
      : categoryError;
  const addDisabled =
    !normalizedNewCategoryName || hasDuplicateCategory || isReservedCategory;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title='Asset Categories'
      subtitle='Create manual categories, then select them when adding assets.'
      icon={<CategoryOutlinedIcon fontSize='small' />}
      maxWidth='sm'
      actions={
        <Button
          variant='outlined'
          onClick={onClose}
          sx={{ textTransform: "none" }}
        >
          Close
        </Button>
      }
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            label='Category name'
            value={newCategoryName}
            onChange={(event) => onCategoryNameChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !addDisabled) {
                event.preventDefault();
                onAddCategory();
              }
            }}
            error={Boolean(validationMessage)}
            helperText={validationMessage}
            fullWidth
            size='small'
          />
          <Button
            variant='contained'
            onClick={onAddCategory}
            disabled={addDisabled}
            sx={{ textTransform: "none", minWidth: 120 }}
          >
            Add
          </Button>
        </Stack>

        <Stack spacing={1}>
          {assetCategories.length ? (
            assetCategories.map((category) => {
              const used = rows.some(
                (row) => row.assetCategoryId === category.categoryId
              );

              return (
                <Stack
                  key={category.categoryId}
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  spacing={1}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant='body2' sx={{ fontWeight: 700 }}>
                      {category.assetCategory}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {used ? "In use" : "Unused"}
                    </Typography>
                  </Box>
                  <Button
                    size='small'
                    color='error'
                    variant='outlined'
                    onClick={() => onRemoveCategory(category.categoryId)}
                    disabled={used}
                    sx={{ textTransform: "none", flexShrink: 0 }}
                  >
                    Remove
                  </Button>
                </Stack>
              );
            })
          ) : (
            <Typography variant='body2' color='text.secondary'>
              No categories created yet.
            </Typography>
          )}
        </Stack>
      </Stack>
    </BaseDialog>
  );
};

export default FaRegisterCategoryDialog;
